import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Play, Plus, Search, Trash2, X } from 'lucide-react';
import { apiClient } from '../api';
import orgApi from '../api/organizations';
import { useAuth } from '../auth/AuthProvider';
import ConfirmModal from '../components/UI/ConfirmModal';
import { errMessage, inputStyle, primaryBtn, primaryBtnStyle, secondaryBtnStyle } from '../components/UI/ink';
import RuleDrawer, { Switch } from '../components/Rules/RuleDrawer';
import TestDrawer from '../components/Rules/TestDrawer';
import {
  CATEGORY_LABEL, NOISY_AT, SEV_LABEL, SEV_RANK, SEV_TOKEN, attackUrl, dayLabel, lastFired, leafShort, leavesOf, matchShort, matchSentence, shortDate,
} from '../components/Rules/model';

// Every rule that checks agent data, how often each fired, and the docked
// pane (?rule=) with what it looks for, its findings and its exceptions.
const mono = 'font-mono text-[10.5px] tracking-[0.12em]';
const panel = { background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' };
const selectStyle = { background: 'var(--surf-surface)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' };
const GRID = 'grid grid-cols-[56px_minmax(0,1fr)_70px_118px_96px] gap-3';

const SevChip = ({ s }) => (
  <span className="inline-flex items-center gap-[5px] h-[22px] px-[7px] rounded-md font-mono text-[10px] font-medium tracking-[0.04em] whitespace-nowrap" style={{ background: `var(--sev-${SEV_TOKEN[s] || 'low'}-bg)`, color: 'var(--ink-text)' }}>
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: `var(--sev-${SEV_TOKEN[s] || 'low'}-fg)` }} />{SEV_LABEL[s] || String(s).toUpperCase()}
  </span>
);

const Bars = ({ values, height, minBar, gap = 2, width }) => {
  const mx = Math.max(1, ...values);
  return values.map((v, i) => (
    <span key={i} className="rounded-[1px]" style={{ width, flex: width ? undefined : '1 1 0', height: v ? Math.max(minBar, Math.round((v / mx) * height)) : 2, background: v ? 'var(--ink-muted)' : 'var(--surf-line-strong)', marginLeft: i ? gap : 0 }} />
  ));
};

export default function DetectionRules() {
  const { permissions = [] } = useAuth();
  const canEdit = permissions.includes('events:update');
  const [searchParams, setSearchParams] = useSearchParams();
  const [rules, setRules] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);
  const [tile, setTile] = useState('all');
  const [src, setSrc] = useState('both');
  const [cat, setCat] = useState('');
  const [sev, setSev] = useState('');
  const [sort, setSort] = useState('findings');
  const [q, setQ] = useState('');
  const [drawer, setDrawer] = useState(null); // { mode, rule } | { test: rule }
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [exForm, setExForm] = useState(null);

  const selectedId = searchParams.get('rule');
  const select = useCallback((id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('rule', id); else next.delete('rule');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get('/api/rules');
      setRules(r.data?.rules || []);
      setError(null);
    } catch (err) {
      setError(errMessage(err, 'Could not load detection rules.'));
      setRules(rules => rules || []);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { orgApi.getMembers().then(m => setMembers(m?.members || m?.users || [])).catch(() => {}); }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      const r = await apiClient.get(`/api/rules/${id}`);
      setDetail(r.data?.rule || null);
    } catch {
      setDetail(false);
    }
  }, []);
  useEffect(() => { setExForm(null); loadDetail(selectedId); }, [selectedId, loadDetail]);
  // Open the first rule when nothing is selected, so the pane is never empty on arrival.
  useEffect(() => { if (!selectedId && rules?.length) select([...rules].sort((a, b) => b.fired_7d - a.fired_7d)[0].id); }, [rules, selectedId, select]);

  const nameOf = useCallback((email) => {
    if (!email) return '';
    const m = members.find(x => x.email?.toLowerCase() === String(email).toLowerCase());
    return m?.full_name || m?.name || email;
  }, [members]);

  const all = useMemo(() => (rules || []).map(r => ({ ...r, noisy: r.enabled && r.fired_7d >= NOISY_AT, silent: r.enabled && r.fired_14d === 0 })), [rules]);
  const onN = all.filter(r => r.enabled).length;
  const builtinN = all.filter(r => r.builtin).length;
  const firing = all.filter(r => r.enabled && r.fired_7d > 0).length;
  const firedTotal = all.reduce((a, r) => a + (r.enabled ? r.fired_7d : 0), 0);
  const tiles = [
    ['all', 'ALL RULES', all.length, `${builtinN} built-in · ${all.length - builtinN} yours`],
    ['on', 'ON', onN, 'checking every snapshot'],
    ['off', 'OFF', all.length - onN, 'not running'],
    ['noisy', 'NOISY', all.filter(r => r.noisy).length, `${NOISY_AT}+ findings in 7 days`],
    ['silent', 'SILENT', all.filter(r => r.silent).length, 'on, nothing in 14 days'],
  ];

  const needle = q.trim().toLowerCase();
  const list = all.filter(r => {
    if (tile === 'on' && !r.enabled) return false;
    if (tile === 'off' && r.enabled) return false;
    if (tile === 'noisy' && !r.noisy) return false;
    if (tile === 'silent' && !r.silent) return false;
    if (src === 'builtin' && !r.builtin) return false;
    if (src === 'yours' && r.builtin) return false;
    if (cat && r.category !== cat) return false;
    if (sev && r.severity !== sev) return false;
    if (needle && ![r.name, r.description, matchShort(r.match), ...(r.mitre_techniques || [])].join(' ').toLowerCase().includes(needle)) return false;
    return true;
  }).sort((a, b) => {
    if (sort === 'last') return String(b.last_fired || '').localeCompare(String(a.last_fired || ''));
    if (sort === 'severity') return (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9) || b.fired_7d - a.fired_7d;
    if (sort === 'name') return a.name.localeCompare(b.name);
    return b.fired_7d - a.fired_7d || Number(b.enabled) - Number(a.enabled);
  });

  const toggle = async (r, enabled) => {
    setRules(rs => rs.map(x => (x.id === r.id ? { ...x, enabled } : x)));
    if (detail?.id === r.id) setDetail(d => ({ ...d, enabled }));
    try {
      await apiClient.patch(`/api/rules/${r.id}/toggle`, { enabled });
    } catch (err) {
      setError(errMessage(err, 'Could not change the rule.'));
      load(); loadDetail(selectedId);
    }
  };

  const del = async () => {
    setBusy(true);
    try {
      await apiClient.delete(`/api/rules/${detail.id}`);
      setConfirmDelete(false);
      select(null);
      await load();
    } catch (err) {
      setError(errMessage(err, 'Could not delete the rule.'));
    } finally {
      setBusy(false);
    }
  };

  const addException = async (ex) => {
    setBusy(true);
    try {
      await apiClient.post(`/api/rules/${detail.id}/exceptions`, ex);
      setExForm(null);
      await Promise.all([loadDetail(detail.id), load()]);
    } catch (err) {
      setExForm(f => ({ ...(f || ex), error: errMessage(err, 'Could not add the exception.') }));
    } finally {
      setBusy(false);
    }
  };
  const removeException = async (id) => {
    try {
      await apiClient.delete(`/api/rules/${detail.id}/exceptions/${id}`);
      await Promise.all([loadDetail(detail.id), load()]);
    } catch (err) {
      setError(errMessage(err, 'Could not remove the exception.'));
    }
  };

  const summary = !rules ? 'Loading…' : `${all.length} rules check every snapshot your agents send. ${onN} are on, and ${firing} of them raised ${firedTotal} finding${firedTotal === 1 ? '' : 's'} in the last 7 days.`;

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-[18px]" style={{ color: 'var(--ink-text)' }}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2 max-w-[760px]">
          <span className="font-mono text-[11.5px] tracking-[0.14em]" style={{ color: 'var(--ink-link)' }}>INTELLIGENCE</span>
          <h1 className="m-0 font-heading text-[30px] font-semibold tracking-[-0.01em] leading-tight">Detection rules</h1>
          <p className="m-0 text-sm leading-normal" style={{ color: 'var(--ink-muted)' }}>{summary}</p>
        </div>
        {canEdit && <button type="button" onClick={() => setDrawer({ mode: 'create', rule: null })} className={primaryBtn} style={primaryBtnStyle}><Plus className="w-4 h-4" strokeWidth={2.2} />Create rule</button>}
      </div>

      {error && <div role="alert" className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-[10px] border text-[13px]" style={{ background: 'var(--sev-crit-bg)', borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)' }}><span>{error}</span><button type="button" onClick={() => setError(null)} aria-label="Dismiss"><X className="w-3.5 h-3.5" /></button></div>}

      <div role="group" aria-label="Filter by status" className="grid gap-2.5 grid-cols-[repeat(auto-fit,minmax(170px,1fr))]">
        {tiles.map(([id, k, n, cap]) => {
          const on = tile === id;
          return (
            <button key={id} type="button" onClick={() => setTile(id)} aria-pressed={on} className="min-h-[84px] box-border flex flex-col items-start gap-1 px-3.5 py-3 rounded-xl border text-left" style={{ background: 'var(--surf-surface)', borderColor: on ? 'var(--accent)' : 'var(--surf-line)' }}>
              <span className="font-mono text-[10.5px] tracking-[0.1em]" style={{ color: 'var(--ink-faint)' }}>{k}</span>
              <span className="font-heading text-[26px] font-semibold leading-none" style={{ color: id === 'noisy' && n ? 'var(--sev-high-fg)' : 'var(--ink-text)' }}>{rules ? n : '—'}</span>
              <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{cap}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-[1_1_280px] max-w-[420px] h-10 box-border flex items-center gap-2 px-3 rounded-[10px] border" style={panel}>
          <Search className="w-[15px] h-[15px] shrink-0" strokeWidth={1.8} style={{ color: 'var(--ink-faint)' }} />
          <input type="search" aria-label="Search rules" placeholder="Rule name, field or ATT&CK ID" value={q} onChange={e => setQ(e.target.value)} className="flex-grow min-w-0 h-[38px] bg-transparent border-0 outline-none text-[13.5px]" style={{ color: 'var(--ink-text)' }} />
        </div>
        <div role="group" aria-label="Who wrote it" className="flex h-10 box-border p-[3px] gap-0.5 rounded-[10px] border" style={panel}>
          {[['both', 'All'], ['builtin', 'Built-in'], ['yours', 'Yours']].map(([id, t]) => {
            const on = src === id;
            return <button key={id} type="button" aria-pressed={on} onClick={() => setSrc(id)} className="h-8 px-3 rounded-[7px] text-[13px]" style={{ background: on ? 'var(--surf-raised)' : 'transparent', color: on ? 'var(--ink-text)' : 'var(--ink-muted)', fontWeight: on ? 600 : 400 }}>{t}</button>;
          })}
        </div>
        <label htmlFor="r-cat" className="sr-only">Category</label>
        <select id="r-cat" value={cat} onChange={e => setCat(e.target.value)} className="h-10 box-border px-2.5 rounded-[10px] border text-[13px]" style={selectStyle}>
          <option value="">Any category</option>{Object.entries(CATEGORY_LABEL).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select>
        <label htmlFor="r-sev" className="sr-only">Severity</label>
        <select id="r-sev" value={sev} onChange={e => setSev(e.target.value)} className="h-10 box-border px-2.5 rounded-[10px] border text-[13px]" style={selectStyle}>
          <option value="">Any severity</option><option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select>
        <label htmlFor="r-sort" className="sr-only">Sort</label>
        <select id="r-sort" value={sort} onChange={e => setSort(e.target.value)} className="md:ml-auto h-10 box-border px-2.5 rounded-[10px] border text-[13px]" style={selectStyle}>
          <option value="findings">Sort: most findings</option><option value="last">Sort: last fired</option><option value="severity">Sort: severity</option><option value="name">Sort: name</option>
        </select>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-[18px] items-start">
        <section aria-label="Rules" className="rounded-[14px] border overflow-hidden min-w-0" style={panel}>
          <div className="overflow-x-auto"><div className="min-w-[620px]">
            <div className={`${GRID} px-4 py-2.5 border-b font-mono text-[10px] tracking-[0.12em]`} style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-faint)' }}><span>ON</span><span>RULE</span><span>SEV</span><span>LAST 7 DAYS</span><span>LAST FIRED</span></div>
            {list.map(r => {
              const sel = r.id === selectedId;
              return (
                <div key={r.id} className={`relative ${GRID} items-center min-h-[68px] px-4 py-2 border-b`} style={{ background: sel ? 'var(--surf-raised)' : 'transparent', borderColor: 'var(--surf-line)' }}>
                  {sel && <span aria-hidden="true" className="absolute left-0 top-3.5 bottom-3.5 w-0.5 rounded-sm" style={{ background: 'var(--accent)' }} />}
                  <Switch on={r.enabled} onClick={() => toggle(r, !r.enabled)} label={`Run ${r.name}`} disabled={!canEdit} />
                  <button type="button" onClick={() => select(r.id)} aria-current={sel ? 'true' : undefined} className="min-w-0 min-h-[52px] flex flex-col justify-center gap-[3px] text-left">
                    <span className="max-w-full text-sm font-semibold truncate" style={{ color: r.enabled ? 'var(--ink-text)' : 'var(--ink-muted)' }}>{r.name}</span>
                    <span className="max-w-full font-mono text-[11.5px] truncate" style={{ color: 'var(--ink-muted)' }}>{matchShort(r.match)}</span>
                    <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{CATEGORY_LABEL[r.category] || r.category} · {r.builtin ? 'Built-in' : nameOf(r.created_by) || 'Yours'}{r.enabled ? '' : ' · off'}{r.exceptions_count ? ` · ${r.exceptions_count} exception${r.exceptions_count === 1 ? '' : 's'}` : ''}</span>
                  </button>
                  <span className="justify-self-start"><SevChip s={r.severity} /></span>
                  <span className="flex items-end gap-2.5">
                    <span className="min-w-[22px] font-heading text-base font-semibold leading-none" style={{ color: r.noisy ? 'var(--sev-high-fg)' : r.fired_7d ? 'var(--ink-text)' : 'var(--ink-faint)' }}>{r.enabled ? r.fired_7d : '–'}</span>
                    <span aria-hidden="true" className="h-5 flex items-end"><Bars values={r.daily.slice(7)} height={20} minBar={4} width={6} /></span>
                  </span>
                  <span className="text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>{lastFired(r.last_fired)}</span>
                </div>
              );
            })}
            {rules && !list.length && <div className="py-10 px-4 text-center text-[13.5px]" style={{ color: 'var(--ink-muted)' }}>{all.length ? 'No rules match these filters.' : 'No detection rules yet.'}</div>}
            {!rules && <div className="py-10 px-4 text-center text-[13.5px]" style={{ color: 'var(--ink-muted)' }}>Loading…</div>}
          </div></div>
          <div className="flex flex-wrap justify-between gap-2 px-4 py-3 text-[12.5px]" style={{ color: 'var(--ink-faint)' }}><span>Showing {list.length} of {all.length} rules</span><span>Findings counted for your organisation only.</span></div>
        </section>

        <RulePane
          r={detail} canEdit={canEdit} nameOf={nameOf} busy={busy} exForm={exForm} setExForm={setExForm}
          onToggle={(on) => toggle(detail, on)} onTest={() => setDrawer({ test: detail })}
          onEdit={() => setDrawer({ mode: 'edit', rule: detail })} onDuplicate={() => setDrawer({ mode: 'duplicate', rule: detail })}
          onDelete={() => setConfirmDelete(true)} onAddException={addException} onRemoveException={removeException}
        />
      </div>

      <RuleDrawer
        open={!!drawer?.mode}
        mode={drawer?.mode}
        rule={drawer?.rule}
        onClose={() => setDrawer(null)}
        onSaved={async (id) => { setDrawer(null); await load(); if (id) { select(id); loadDetail(id); } }}
      />
      <TestDrawer open={!!drawer?.test} rule={drawer?.test} onClose={() => setDrawer(null)} />
      <ConfirmModal
        isOpen={confirmDelete}
        title="Delete this rule?"
        message={detail ? `${detail.name} stops checking anything. The ${detail.total_findings} finding${detail.total_findings === 1 ? '' : 's'} it raised stay where they are.` : ''}
        confirmLabel={busy ? 'Deleting…' : 'Delete rule'}
        danger
        disabled={busy}
        onConfirm={del}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

function RulePane({ r, canEdit, nameOf, busy, exForm, setExForm, onToggle, onTest, onEdit, onDuplicate, onDelete, onAddException, onRemoveException }) {
  if (r === false) return <aside aria-label="Selected rule" className="rounded-[14px] border p-8 text-center text-sm" style={{ ...panel, color: 'var(--ink-muted)' }}>This rule couldn’t be loaded. It may have been deleted.</aside>;
  if (!r) return <aside aria-label="Selected rule" className="rounded-[14px] border min-h-[300px] flex items-center justify-center p-8 text-center text-sm" style={{ ...panel, color: 'var(--ink-muted)' }}>Select a rule to see what it looks for and what it found.</aside>;

  const h3 = `m-0 ${mono} font-medium`;
  const chartNote = r.fired_14d ? `${r.fired_7d} this week · ${r.assets_7d} asset${r.assets_7d === 1 ? '' : 's'}` : 'None';
  const made = r.builtin
    ? `Ships with DragonEye. Updated with the platform.${r.overridden ? ` Turned ${r.enabled ? 'on' : 'off'} for your organisation${r.override_by ? ` by ${nameOf(r.override_by)}` : ''}${r.override_at ? `, ${shortDate(r.override_at)}` : ''}.` : ''}`
    : `Created ${shortDate(r.created_at)}${r.created_by ? ` by ${nameOf(r.created_by)}` : ''}${r.updated_by && r.updated_at && r.updated_at !== r.created_at ? ` · edited ${shortDate(r.updated_at)} by ${nameOf(r.updated_by)}` : ''}`;
  const sg = r.suggestion;
  const btn = 'h-10 inline-flex items-center gap-[7px] px-3.5 rounded-[9px] border text-[13.5px] font-medium disabled:opacity-50';

  return (
    <aside aria-label="Selected rule" className="rounded-[14px] border box-border flex flex-col gap-[18px] p-5 min-w-0" style={panel}>
      <div className="flex flex-col gap-2">
        <span className={mono} style={{ color: 'var(--ink-faint)' }}>{r.builtin ? 'RULE · BUILT-IN' : `RULE · BY ${String(nameOf(r.created_by) || 'YOUR TEAM').toUpperCase()}`}</span>
        <h2 className="m-0 font-heading text-xl font-semibold leading-tight break-words">{r.name}</h2>
        <span className="flex flex-wrap items-center gap-1.5">
          <SevChip s={r.severity} />
          <span className="h-[22px] inline-flex items-center px-[7px] rounded-md border text-xs" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>{CATEGORY_LABEL[r.category] || r.category}</span>
          {(r.mitre_techniques || []).map(t => <a key={t} href={attackUrl(t)} target="_blank" rel="noreferrer" className="h-[22px] inline-flex items-center px-[7px] rounded-md border font-mono text-[11px] no-underline" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-link)' }}>{t}</a>)}
        </span>
      </div>

      <div className="flex items-center gap-3 px-3.5 py-3 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
        <span className="flex-grow flex flex-col gap-0.5"><span className="text-[13.5px] font-medium">{r.enabled ? 'On for your organisation' : 'Off for your organisation'}</span>
          <span className="text-xs leading-snug" style={{ color: 'var(--ink-faint)' }}>{r.builtin ? 'Built-in rules can’t be edited. Turning one off only affects your organisation.' : r.enabled ? 'Checks every snapshot from every agent.' : 'Saved, but not checking anything.'}</span>
        </span>
        <Switch on={r.enabled} onClick={() => onToggle(!r.enabled)} label="Run this rule" disabled={!canEdit} />
      </div>

      <div className="flex flex-col gap-2">
        <h3 className={h3} style={{ color: 'var(--ink-faint)' }}>WHAT IT LOOKS FOR</h3>
        {r.description && <p className="m-0 text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{r.description}</p>}
        <p className="m-0 text-sm leading-relaxed">{matchSentence(r.match)}</p>
        <div className="flex flex-col gap-1.5 pt-1">
          {leavesOf(r.match).map((l, i) => (
            <span key={i} className="flex flex-wrap items-center gap-1.5">
              {i > 0 && <span className="w-[34px] font-mono text-[10.5px]" style={{ color: 'var(--ink-faint)' }}>{r.match.logic === 'any' ? 'OR' : 'AND'}</span>}
              <span className="font-mono text-[11.5px] px-2 py-1 rounded-md break-all" style={{ background: 'var(--surf-raised)' }}>{leafShort(l)}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <span className="flex items-baseline justify-between gap-2"><h3 className={h3} style={{ color: 'var(--ink-faint)' }}>FINDINGS, LAST 14 DAYS</h3><span className="text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>{chartNote}</span></span>
        <div role="img" aria-label={`${r.fired_14d} findings in the last 14 days, ${r.fired_7d} in the last 7`} className="h-[68px] flex items-end pb-1.5 border-b" style={{ borderColor: 'var(--surf-line)' }}><Bars values={r.daily} height={60} minBar={5} gap={4} /></div>
        <span className="flex justify-between font-mono text-[10.5px]" style={{ color: 'var(--ink-faint)' }}><span>{dayLabel(13)}</span><span>{dayLabel(6)}</span><span>Today</span></span>
      </div>

      {sg && canEdit && (
        <div className="flex flex-col gap-2.5 p-3.5 rounded-[10px]" style={{ background: 'var(--sev-high-bg)' }}>
          <span className="text-[13px] leading-normal">{sg.text}</span>
          <span className="flex flex-wrap gap-2">
            {sg.exception && <button type="button" onClick={() => setExForm({ ...sg.exception, reason: '' })} className="h-9 px-3 rounded-lg border text-[13px]" style={{ background: 'var(--surf-surface)', ...secondaryBtnStyle }}>{sg.label}</button>}
            <button type="button" onClick={onDuplicate} className="h-9 px-3 rounded-lg border text-[13px]" style={secondaryBtnStyle}>Duplicate and narrow it</button>
          </span>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <h3 className={`${h3} mb-1`} style={{ color: 'var(--ink-faint)' }}>RECENT FINDINGS</h3>
        {(r.recent || []).map(f => (
          <div key={f.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2.5 gap-y-0.5 py-2 border-b" style={{ borderColor: 'var(--surf-line)' }}>
            <Link to={`/findings?finding=${f.id}`} className="font-mono text-xs" style={{ color: 'var(--ink-link)' }}>{f.id}</Link>
            <span className="text-xs text-right" style={{ color: 'var(--ink-faint)' }}>{lastFired(f.detected_at)}</span>
            <span className="min-w-0 text-[13px] truncate">{f.hostname || f.server_id}{f.entity && f.entity !== r.id ? <> · <span className="font-mono text-xs" style={{ color: 'var(--ink-muted)' }}>{f.entity}</span></> : null}</span>
            {f.case_id ? <Link to={`/cases/${f.case_id}`} className="font-mono text-[11.5px] text-right" style={{ color: 'var(--ink-link)' }}>{String(f.case_id).toUpperCase()}</Link> : <span />}
          </div>
        ))}
        {!(r.recent || []).length && <span className="text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{r.enabled ? 'No findings yet. Use Test to check it matches what you expect.' : 'This rule is off, so it hasn’t checked anything.'}</span>}
        {r.total_findings > 0 && <Link to={`/findings?rule=${r.id}`} className="self-start mt-1.5 text-[13px]" style={{ color: 'var(--ink-link)' }}>All {r.total_findings} finding{r.total_findings === 1 ? '' : 's'} from this rule →</Link>}
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="flex items-center justify-between"><h3 className={h3} style={{ color: 'var(--ink-faint)' }}>EXCEPTIONS</h3>
          {canEdit && !exForm && <button type="button" onClick={() => setExForm({ field: 'system.hostname', operator: 'equals', value: '', reason: '' })} className="h-8 text-[13px]" style={{ color: 'var(--ink-link)' }}>+ Add exception</button>}
        </span>
        {(r.exceptions || []).map(x => (
          <div key={x.id} className="flex items-start gap-2 px-3 py-2.5 rounded-lg border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
            <span className="flex-grow flex flex-col gap-[3px] min-w-0">
              <span className="font-mono text-xs break-all">{x.field === 'entity' ? 'matched value' : x.field} {x.operator.replace('_', ' ')} {x.value}</span>
              <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{x.reason}{x.created_by ? ` · ${nameOf(x.created_by)}, ${shortDate(x.created_at)}` : ''}</span>
            </span>
            {canEdit && <button type="button" onClick={() => onRemoveException(x.id)} aria-label="Remove exception" className="w-8 h-8 shrink-0 flex items-center justify-center" style={{ color: 'var(--ink-faint)' }}><Trash2 className="w-[15px] h-[15px]" strokeWidth={1.8} /></button>}
          </div>
        ))}
        {!(r.exceptions || []).length && !exForm && <span className="text-[13px]" style={{ color: 'var(--ink-muted)' }}>None. Every match raises a finding.</span>}
        {exForm && <ExceptionForm f={exForm} set={setExForm} busy={busy} onSave={onAddException} />}
      </div>

      {(r.false_positive_hints || []).length > 0 && (
        <div className="flex flex-col gap-1.5">
          <h3 className={h3} style={{ color: 'var(--ink-faint)' }}>KNOWN FALSE POSITIVES</h3>
          {r.false_positive_hints.map((t, i) => <span key={i} className="text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{t}</span>)}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <button type="button" onClick={onTest} className={btn} style={secondaryBtnStyle}><Play className="w-3.5 h-3.5" strokeWidth={2} />Test</button>
        {canEdit && !r.builtin && <button type="button" onClick={onEdit} className={btn} style={secondaryBtnStyle}>Edit</button>}
        {canEdit && <button type="button" onClick={onDuplicate} className={btn} style={secondaryBtnStyle}>Duplicate</button>}
        {canEdit && !r.builtin && <button type="button" onClick={onDelete} className="ml-auto h-10 px-3 text-[13.5px] font-medium" style={{ color: 'var(--sev-crit-fg)' }}>Delete</button>}
      </div>
      <span className="text-xs leading-normal" style={{ color: 'var(--ink-faint)' }}>{made}</span>
    </aside>
  );
}

function ExceptionForm({ f, set, busy, onSave }) {
  const upd = (k) => (e) => set(x => ({ ...x, [k]: e.target.value, error: null }));
  const field = { ...inputStyle, height: 38 };
  return (
    <div className="flex flex-col gap-2 p-3 rounded-[10px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--accent)' }}>
      <span className="text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>Don’t raise a finding when</span>
      <div className="flex flex-wrap gap-2">
        <select aria-label="Exception field" value={f.field} onChange={upd('field')} className="flex-[1_1_140px] box-border px-2 rounded-lg border text-[13px]" style={field}>
          <option value="system.hostname">the hostname</option><option value="entity">the matched value</option>
        </select>
        <select aria-label="Exception check" value={f.operator} onChange={upd('operator')} className="flex-[1_1_120px] box-border px-2 rounded-lg border text-[13px]" style={field}>
          <option value="equals">equals</option><option value="starts_with">starts with</option><option value="contains">contains</option><option value="ends_with">ends with</option><option value="regex">matches regex</option>{f.field === 'entity' && <option value="ip_in_cidr">is in IP range</option>}
        </select>
        <input type="text" aria-label="Exception value" value={f.value} onChange={upd('value')} placeholder={f.field === 'entity' ? '203.0.113.45' : 'lab-pc-'} className="flex-[1_1_120px] min-w-0 box-border px-2.5 rounded-lg border font-mono text-[12.5px] outline-none" style={field} />
      </div>
      <input type="text" aria-label="Why" value={f.reason} onChange={upd('reason')} placeholder="Why, e.g. RDP is open on purpose on lab PCs" className="box-border px-2.5 rounded-lg border text-[13px] outline-none" style={field} />
      {f.error && <span role="alert" className="text-[12.5px]" style={{ color: 'var(--sev-crit-fg)' }}>{f.error}</span>}
      <span className="flex gap-2">
        <button type="button" disabled={busy || !f.value.trim() || !f.reason.trim()} onClick={() => onSave({ field: f.field, operator: f.operator, value: f.value.trim(), reason: f.reason.trim() })} className="h-9 px-3 rounded-lg text-[13px] font-semibold disabled:opacity-50" style={primaryBtnStyle}>{busy ? 'Adding…' : 'Add exception'}</button>
        <button type="button" onClick={() => set(null)} className="h-9 px-2 text-[13px]" style={{ color: 'var(--ink-muted)' }}>Cancel</button>
      </span>
    </div>
  );
}
